import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from './Spinner';

const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  confirmButtonClass = 'bg-blue-600 hover:bg-blue-700 text-white',
  isLoading = false
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const modalTitle = title || t('confirmationModal.defaultTitle');
  const modalMessage = message || t('confirmationModal.defaultMessage');
  const confirmButtonText = confirmText || t('confirmationModal.confirm');
  const cancelButtonText = cancelText || t('confirmationModal.cancel');
  const processingText = t('common.status.processing', 'Processing...');

  // Escape key closes the modal (unless mid-action)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isLoading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={() => { if (!isLoading) onCancel(); }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h2 className="heading-mono text-xl mb-4 text-gray-900 dark:text-gray-100">
            {modalTitle}
          </h2>

          <div className="text-mono text-sm mb-6 text-gray-600 dark:text-gray-300">
            {typeof modalMessage === 'string' ? <p>{modalMessage}</p> : modalMessage}
          </div>

          <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 px-4 py-2 rounded-lg text-mono font-medium disabled:opacity-50 transition-colors"
            >
              {cancelButtonText}
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className={`flex-1 px-4 py-2 rounded-lg text-mono font-medium disabled:opacity-50 ${confirmButtonClass}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Spinner size="sm" />
                  <span>{processingText}</span>
                </div>
              ) : (
                confirmButtonText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
